import { defineStore } from 'pinia'

export interface Note {
  id: number
  bubbleUuid: string
  intentions?: string
  important?: string
  priority?: number
}

export interface NoteAdd {
  bubbleUuid: string
  intentions?: string
  important?: string
  priority?: number
}

export interface NoteUpdate {
  intentions?: string
  important?: string
  priority?: number
}

export const useNotesStore = defineStore('notesStore', {
  state: () => ({
    notes: {} as Record<string, Note>,
    loading: false,
    error: null as string | null,
  }),
  actions: {
    async addNote(note: NoteAdd): Promise<{ success: boolean; error?: string; note?: Note }> {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(`${useRuntimeConfig().public.apiBase}/notes/add`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(note),
        })
        if (response.ok) {
          const newNote: Note = await response.json()
          this.notes[newNote.id] = newNote
          return { success: true, note: newNote }
        } else {
          const errorData = await response.json()
          return {
            success: false,
            error: errorData.detail || 'Failed to add note',
          }
        }
      } catch (error) {
        this.error = 'Error adding note'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async getNoteById(noteId: number): Promise<Note | null> {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(`${useRuntimeConfig().public.apiBase}/notes/${noteId}`)
        if (response.ok) {
          const note: Note = await response.json()
          this.notes[noteId] = note
          return note
        } else {
          throw new Error('Note not found')
        }
      } catch (error) {
        this.error = 'Error fetching note'
        return null
      } finally {
        this.loading = false
      }
    },

    async getNoteByBubbleUuid(bubbleUuid: string): Promise<Note | null> {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/notes/uuid/${bubbleUuid}`
        )
        if (response.ok) {
          const note: Note | null = await response.json()
          if (note) {
            this.notes[note.id] = note
          }
          return note
        } else {
          return null
        }
      } catch (error) {
        this.error = 'Error fetching note by bubble UUID'
        return null
      } finally {
        this.loading = false
      }
    },

    async updateNote(
      noteId: number,
      noteUpdate: NoteUpdate
    ): Promise<{ success: boolean; error?: string; note?: Note }> {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(`${useRuntimeConfig().public.apiBase}/notes/${noteId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(noteUpdate),
        })
        if (response.ok) {
          const updatedNote: Note = await response.json()
          this.notes[noteId] = updatedNote
          return { success: true, note: updatedNote }
        } else {
          const errorData = await response.json()
          return {
            success: false,
            error: errorData.detail || 'Failed to update note',
          }
        }
      } catch (error) {
        this.error = 'Error updating note'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async deleteNote(noteId: number): Promise<{ success: boolean; error?: string }> {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(`${useRuntimeConfig().public.apiBase}/notes/${noteId}`, {
          method: 'DELETE',
        })
        if (response.ok) {
          this.notes = Object.fromEntries(
            Object.entries(this.notes).filter(([key]) => key !== noteId.toString())
          )
          return { success: true }
        } else {
          const errorData = await response.json()
          return {
            success: false,
            error: errorData.detail || 'Failed to delete note',
          }
        }
      } catch (error) {
        this.error = 'Error deleting note'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async deleteNotesByBubbleUuid(
      bubbleUuid: string
    ): Promise<{ success: boolean; error?: string; deletedCount?: number }> {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/notes/uuid/${bubbleUuid}`,
          {
            method: 'DELETE',
          }
        )
        if (response.ok) {
          const result = await response.json()
          // Remove deleted notes from the store
          this.notes = Object.fromEntries(
            Object.entries(this.notes).filter(([, note]) => note.bubbleUuid !== bubbleUuid)
          )
          return { success: true, deletedCount: result.deletedCount }
        } else {
          const errorData = await response.json()
          return {
            success: false,
            error: errorData.detail || 'Failed to delete notes',
          }
        }
      } catch (error) {
        this.error = 'Error deleting notes by bubble UUID'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async updateNotePriority(
      noteId: number,
      newPriority: number
    ): Promise<{ success: boolean; error?: string; note?: Note }> {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/notes/${noteId}/priority`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ newPriority }),
          }
        )
        if (response.ok) {
          const updatedNote: Note = await response.json()
          this.notes[noteId] = updatedNote
          return { success: true, note: updatedNote }
        } else {
          const errorData = await response.json()
          return {
            success: false,
            error: errorData.detail || 'Failed to update note priority',
          }
        }
      } catch (error) {
        this.error = 'Error updating note priority'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async getNotesByPriority(priority: number): Promise<Note[]> {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/notes/priority/${priority}`
        )
        if (response.ok) {
          const notes: Note[] = await response.json()
          notes.forEach((note: Note) => {
            this.notes[note.id] = note
          })
          return notes
        } else {
          throw new Error('Failed to fetch notes by priority')
        }
      } catch (error) {
        this.error = 'Error fetching notes by priority'
        return []
      } finally {
        this.loading = false
      }
    },
  },
})
